'use client';

import { Tooltip as TooltipPrimitive } from '@base-ui/react/tooltip';
import { cn } from '@zealot/ui/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';

const tooltipVariants = cva(
  'z-50 inline-flex w-fit items-center overflow-hidden rounded-md px-3 py-1.5 text-xs ' +
    'data-starting-style:opacity-0 data-ending-style:opacity-0 ' +
    'data-starting-style:scale-95 data-ending-style:scale-95 ' +
    'transition-[scale,opacity] duration-150 origin-[var(--transform-origin)]',
  {
    variants: {
      variant: {
        default: 'bg-foreground text-background [&>[data-slot=tooltip-arrow]]:fill-foreground',
        outline:
          'border bg-popover text-popover-foreground text-sm shadow-md [&>[data-slot=tooltip-arrow]]:fill-popover',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

const TooltipProvider = TooltipPrimitive.Provider;

const Tooltip = TooltipPrimitive.Root;

const TooltipTrigger = ({ className, ...props }: TooltipPrimitive.Trigger.Props) => {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" className={className} {...props} />;
};

const TooltipPortal = TooltipPrimitive.Portal;

// gloss
const TooltipPositioner = ({ className, ...props }: TooltipPrimitive.Positioner.Props) => {
  return (
    <TooltipPrimitive.Positioner data-slot="tooltip-positioner" className={cn('isolate z-50', className)} {...props} />
  );
};

type TooltipPopupProps = TooltipPrimitive.Popup.Props & VariantProps<typeof tooltipVariants>;

const TooltipPopup = ({ className, variant = 'default', ...props }: TooltipPopupProps) => {
  return (
    <TooltipPrimitive.Popup
      data-slot="tooltip-popup"
      className={cn(tooltipVariants({ variant, className }))}
      {...props}
    />
  );
};

const TooltipArrow = ({ className, ...props }: TooltipPrimitive.Arrow.Props) => {
  return <TooltipPrimitive.Arrow data-slot="tooltip-arrow" className={className} {...props} />;
};

type TooltipContentProps = TooltipPrimitive.Popup.Props &
  VariantProps<typeof tooltipVariants> & {
    side?: 'top' | 'bottom' | 'left' | 'right' | 'inline-end' | 'inline-start';
    sideOffset?: number;
    align?: 'start' | 'center' | 'end';
    alignOffset?: number;
    collisionPadding?: TooltipPrimitive.Positioner.Props['collisionPadding'];
    hasArrow?: boolean;
  };

const TooltipContent = ({
  className,
  variant = 'default',
  side = 'top',
  sideOffset = 4,
  align = 'center',
  alignOffset = 0,
  collisionPadding,
  hasArrow = true,
  children,
  ...props
}: TooltipContentProps) => {
  return (
    <TooltipPortal>
      <TooltipPositioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        collisionPadding={collisionPadding}
      >
        <TooltipPopup className={className} variant={variant} {...props}>
          {children}
          {hasArrow && <TooltipArrow />}
        </TooltipPopup>
      </TooltipPositioner>
    </TooltipPortal>
  );
};

export type { TooltipContentProps };
export {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipPopup,
  TooltipPortal,
  TooltipPositioner,
  TooltipProvider,
  TooltipTrigger,
  tooltipVariants,
};
